import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { DBConfig } from '@/config/indexdb-config'
import Provider, { router } from '@/providers'
import '@/styles/global.css'
import { initDB } from 'react-indexed-db-hook'
import { initPosthog } from '@/services/posthog'

// Industry-standard auto-recovery from stale chunk/module errors during deploys.
// Uses sessionStorage guard to prevent infinite reload loops (max 1 reload per session).
;(() => {
  const RELOAD_KEY = '__chunk_reload__';
  const safeReload = () => {
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    // Only auto-reload once every 10 seconds to prevent infinite loops
    if (!lastReload || now - Number(lastReload) > 10_000) {
      sessionStorage.setItem(RELOAD_KEY, String(now));
      window.location.reload();
    }
  };
  window.addEventListener('error', (e) => {
    if (e.message && (
      e.message.includes('Failed to fetch dynamically imported module') ||
      e.message.includes('Expected a JavaScript-or-Wasm module script') ||
      e.message.includes('Failed to load module script') ||
      e.message.includes('error loading dynamically imported module') ||
      e.message.includes('Importing a module script failed')
    )) { safeReload(); }
  }, true);
  window.addEventListener('vite:preloadError', safeReload);
})();

import * as Sentry from '@sentry/react'
import config from '@/config/config.env'

if (config.sentryConfig?.dsn) {
  Sentry.init({
    dsn: config.sentryConfig.dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (typeof window !== 'undefined' && (window as any).posthog) {
        const ph = (window as any).posthog;
        const sessionId = ph.get_session_id?.();
        const distinctId = ph.get_distinct_id?.();
        if (sessionId) {
          event.tags = {
            ...event.tags,
            'posthog_session_id': sessionId,
            'posthog_distinct_id': distinctId,
          };
          event.extra = {
            ...event.extra,
            'PostHog Session Replay': `https://us.posthog.com/project/352096/replay/${sessionId}`,
          };
        }
      }
      return event;
    },
  })
}

// TRIGGER REBUILD: Force Vercel compile to load latest Doppler PostHog secrets
initDB(DBConfig)
initPosthog()

const rootElement = document.getElementById('root')!
const root = ReactDOM.createRoot(rootElement)
root.render(
  <StrictMode>
    <Provider>
      <RouterProvider router={router} />
    </Provider>
  </StrictMode>
)
